import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { formatOrdinalDate } from "../lib/timeFormat";
import { CheckCircle2, MapPin, Calendar, Ticket, Lock, KeyRound } from "lucide-react";

export default function GuestListSignupPage() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const urlCode = sp.get("code") || "";

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [invite, setInvite] = useState(null);      // validated invite {name,email,status,order_id}
  const [codeInput, setCodeInput] = useState(urlCode);
  const [codeErr, setCodeErr] = useState("");
  const [checking, setChecking] = useState(false);

  const [dateId, setDateId] = useState("");
  const [name, setName] = useState("");
  const [addGuests, setAddGuests] = useState(0);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [inline, setInline] = useState("");

  useEffect(() => {
    api.get(`/public/guest-list/${token}`).then((r) => {
      setData(r.data);
      if ((r.data.dates || []).length === 1) setDateId(r.data.dates[0].id);
    }).catch(() => setErr("This guest list isn't available."));
  }, [token]);

  useEffect(() => {
    if (urlCode) validateCode(urlCode);
    // eslint-disable-next-line
  }, [urlCode]);

  const validateCode = async (code) => {
    if (!code.trim()) return setCodeErr("Enter your invite code");
    setChecking(true); setCodeErr("");
    try {
      const r = await api.get(`/public/guest-list/${token}/invite/${code.trim().toUpperCase()}`);
      setInvite(r.data);
      setName(r.data.name || "");
      if (r.data.custom_fields && typeof r.data.custom_fields === "object") setCustom(r.data.custom_fields);
    } catch (e) {
      setCodeErr(formatErr(e.response?.data?.detail) || "That invite code isn't valid");
    } finally { setChecking(false); }
  };

  if (err) return <Wrap><div className="max-w-md mx-auto p-6 text-center text-[#fca5a5] py-20" data-testid="gl-error">{err}</div></Wrap>;
  if (!data) return <Wrap><div className="text-center text-[#8a8a8a] py-20">Loading…</div></Wrap>;

  const gl = data.guest_list;
  const attraction = data.attraction;
  const maxAdd = gl.max_additional_guests || 0;

  // Success screen
  if (done) return (
    <Wrap>
      <div className="max-w-md mx-auto p-5" data-testid="gl-success">
        <Brand />
        <div className="st-card p-8 text-center st-fade-up">
          <div className="w-14 h-14 rounded-full bg-[#0c1d12] border border-[#1b3a25] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={26} className="text-[#86efac]" />
          </div>
          <h1 className="text-xl font-bold">You're on the {gl.title} list!</h1>
          <p className="text-sm text-[#bbb] mt-2">We've booked {done.party_size} {done.party_size === 1 ? "ticket" : "tickets"} and emailed them to <strong>{invite?.email}</strong>.</p>
          <a href={`/order/${done.order_id}/tickets`} className="st-btn mt-5 inline-flex" data-testid="view-tickets-btn"><Ticket size={15} /> View my tickets</a>
        </div>
      </div>
    </Wrap>
  );

  // Already claimed
  if (invite && invite.status === "accepted") return (
    <Wrap>
      <div className="max-w-md mx-auto p-5" data-testid="gl-already-claimed">
        <Brand />
        <div className="st-card p-8 text-center st-fade-up">
          <CheckCircle2 size={26} className="text-[#86efac] mx-auto mb-3" />
          <h1 className="text-xl font-bold">You've already claimed your spot</h1>
          <p className="text-sm text-[#bbb] mt-2">This invite for <strong>{invite.email}</strong> has already been used.</p>
          {invite.order_id && <a href={`/order/${invite.order_id}/tickets`} className="st-btn mt-5 inline-flex" data-testid="view-tickets-btn"><Ticket size={15} /> View my tickets</a>}
        </div>
      </div>
    </Wrap>
  );

  // Code gate (no valid invite yet)
  if (!invite) return (
    <Wrap>
      <div className="max-w-md mx-auto p-5" data-testid="gl-code-gate">
        <Brand />
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#dc2626]">{gl.title} Guest List</div>
          <div className="text-2xl font-bold mt-1">{attraction.name}</div>
        </div>
        <div className="st-card p-6 st-fade-up text-center">
          <div className="w-12 h-12 rounded-full bg-[#141414] border border-[#1a1a1a] flex items-center justify-center mx-auto mb-3"><Lock size={20} className="text-[#dc2626]" /></div>
          <h1 className="text-lg font-bold">Invite only</h1>
          <p className="text-sm text-[#8a8a8a] mt-1 mb-4">Enter the invite code from your email to claim your tickets.</p>
          {codeErr && <div className="text-sm text-[#fca5a5] bg-[#2a0e0e] border border-[#dc2626]/40 rounded-lg px-3 py-2 mb-3" data-testid="gl-code-error">{codeErr}</div>}
          <input
            className="st-input text-center tracking-[0.3em] st-mono uppercase"
            placeholder="ABCD1234"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && validateCode(codeInput)}
            data-testid="gl-code-input"
          />
          <button className="st-btn w-full mt-3" onClick={() => validateCode(codeInput)} disabled={checking} data-testid="gl-code-submit">
            <KeyRound size={15} /> {checking ? "Checking…" : "Continue"}
          </button>
        </div>
        <p className="text-center text-xs text-[#7a7a7a] mt-6">Powered by ScareTickets</p>
      </div>
    </Wrap>
  );

  // Claim form (valid invite)
  const submit = async () => {
    if (!dateId) return setErr2("Please pick a date");
    for (const f of gl.custom_fields || []) {
      if (f.required && !(custom[f.key] || "").trim()) return setErr2(`${f.label} is required`);
    }
    setBusy(true); setInline("");
    try {
      const r = await api.post(`/public/guest-list/${token}/signup`, {
        name: name.trim(), code: invite ? codeInput || urlCode : "", event_date_id: dateId,
        additional_guests: Number(addGuests) || 0, custom_fields: custom,
      });
      setDone(r.data);
    } catch (e) { setErr2(formatErr(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };
  function setErr2(m) { window.scrollTo({ top: 0 }); setInline(m); }

  return (
    <Wrap>
      <div className="max-w-md mx-auto p-5" data-testid="gl-signup-page">
        <Brand />
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#dc2626]">{gl.title} Guest List</div>
          <div className="text-2xl font-bold mt-1">{attraction.name}</div>
          <div className="text-xs text-[#8a8a8a] flex items-center gap-1 justify-center mt-1"><MapPin size={12} />{attraction.location}</div>
          {gl.description && <p className="text-sm text-[#bbb] mt-3">{gl.description}</p>}
        </div>

        <div className="st-card p-5 space-y-4 st-fade-up">
          {inline && <div className="text-sm text-[#fca5a5] bg-[#2a0e0e] border border-[#dc2626]/40 rounded-lg px-3 py-2" data-testid="gl-inline-error">{inline}</div>}

          <div>
            <label className="text-xs uppercase tracking-wider text-[#8a8a8a] block mb-1.5">Choose your night</label>
            {data.dates.length === 0 ? (
              <p className="text-sm text-[#8a8a8a]">No dates available right now.</p>
            ) : (
              <div className="space-y-2" data-testid="gl-date-options">
                {data.dates.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setDateId(d.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-sm transition-colors ${dateId === d.id ? "border-[#dc2626] bg-[#dc2626]/10" : "border-[#1a1a1a] hover:border-[#333]"}`}
                    data-testid={`gl-date-option-${d.id}`}
                  >
                    <Calendar size={16} className={dateId === d.id ? "text-[#dc2626]" : "text-[#8a8a8a]"} />
                    <span className="font-medium">{formatOrdinalDate(d.date)}</span>
                    {dateId === d.id && <CheckCircle2 size={16} className="text-[#dc2626] ml-auto" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#8a8a8a] block mb-1.5">Your name</label>
            <input className="st-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="gl-name-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8a8a8a] block mb-1.5 flex items-center gap-1"><Lock size={11} /> Email (from your invite)</label>
            <input className="st-input opacity-60 cursor-not-allowed" value={invite.email} readOnly data-testid="gl-email-locked" />
          </div>

          {maxAdd > 0 && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8a8a8a] block mb-1.5">Additional guests (up to {maxAdd})</label>
              <select className="st-input" value={addGuests} onChange={(e) => setAddGuests(e.target.value)} data-testid="gl-guests-select">
                {Array.from({ length: maxAdd + 1 }, (_, i) => <option key={i} value={i}>{i === 0 ? "Just me" : `+${i} guest${i > 1 ? "s" : ""}`}</option>)}
              </select>
            </div>
          )}

          {(gl.custom_fields || []).map((f) => (
            <div key={f.key}>
              <label className="text-xs uppercase tracking-wider text-[#8a8a8a] block mb-1.5">{f.label}{f.required ? " *" : ""}</label>
              <input className="st-input" value={custom[f.key] || ""} onChange={(e) => setCustom((p) => ({ ...p, [f.key]: e.target.value }))} data-testid={`gl-custom-${f.key}`} />
            </div>
          ))}

          <button className="st-btn w-full" onClick={submit} disabled={busy} data-testid="gl-submit-btn">
            {busy ? "Booking…" : `Get my ticket${Number(addGuests) > 0 ? "s" : ""}`}
          </button>
          <p className="text-center text-[11px] text-[#7a7a7a]">Free guest-list entry · QR ticket sent to your email</p>
        </div>
        <p className="text-center text-xs text-[#7a7a7a] mt-6">Powered by ScareTickets</p>
      </div>
    </Wrap>
  );
}

function Brand() {
  return <div className="text-center mb-6"><div className="st-brand text-3xl text-white">SCARE<span className="text-[#dc2626]">TICKETS</span></div></div>;
}
function Wrap({ children }) {
  return <div className="min-h-screen st-grain bg-[#0a0a0a] py-10">{children}</div>;
}
